/**
 * @fileoverview Main Sandbox class with E2B-style API
 */

import { EventEmitter } from 'eventemitter3'
import {
  TechStack,
  SandboxStatus,
  SandboxConfig,
  CreateSandboxOptions,
  Sandbox as SandboxInfo,
  ExecutionResult,
  ExecuteCodeOptions,
  StreamExecutionOptions,
  FileInfo,
  FileOperationOptions,
  WatchOptions,
  FileChangeEvent,
  OperationProgress,
  BulkOperationOptions,
  JobProgress,
  SandboxClientConfig,
  WebSocketEventType
} from './types'
import { HttpClient, WebSocketClient } from './client'
import {
  SandboxError,
  FileError,
  ValidationError,
  NetworkError
} from './errors'
import { SandboxFileSystem, FileSystemAPI } from './filesystem'
import { ProcessManager, SandboxProcess, SpawnOptions } from './process'
import { PreviewManager, PreviewScriptOptions, PreviewError, PortChangeEvent } from './preview'
import { HMRManager, HMROptions, HMRUpdate, HMRStateSnapshot } from './hmr'
import { MultiTabSyncManager, SyncOptions, FileSyncChange, CursorSyncInfo, TabInfo } from './sync'

/**
 * Main Sandbox class - provides E2B-style API for sandbox management
 */
export class Sandbox extends EventEmitter {
  private httpClient: HttpClient
  private wsClient?: WebSocketClient
  private sandboxInfo?: SandboxInfo
  private _isReady = false
  private _fs?: SandboxFileSystem
  private _process?: ProcessManager
  private _previewManager?: PreviewManager
  private _hmrManager?: HMRManager
  private _syncManager?: MultiTabSyncManager

  // Static fields for global request deduplication
  private static pendingCreations = new Map<string, Promise<Sandbox>>()
  private static globalHttpClient?: HttpClient

  constructor(
    sandboxInfo: SandboxInfo,
    config: SandboxClientConfig = {}
  ) {
    super()
    this.sandboxInfo = sandboxInfo
    this.httpClient = new HttpClient(config)
    this._isReady = sandboxInfo.status === SandboxStatus.RUNNING

    // Initialize WebSocket if needed
    if (config.debug) {
      console.log('[SDK] Sandbox initialized:', sandboxInfo.id)
    }
  }

  /**
   * Create a new sandbox instance (E2B-style static factory method)
   */
  static async create(
    config: SandboxConfig & SandboxClientConfig & CreateSandboxOptions = { techStack: TechStack.NODEJS }
  ): Promise<Sandbox> {
    // Validate configuration
    if (!config.techStack) {
      throw ValidationError.missingRequiredField('techStack')
    }

    // Create sandbox via API - match backend Joi schema structure
    const createPayload: any = {
      techStack: config.techStack,
      userId: config.userId,
      projectName: config.projectName,
      initialFiles: config.initialFiles || {}
    }

    // Build resources object if any resource limits are provided
    const resources: any = {}
    if (config.cpuLimit !== undefined) {
      resources.cpu = config.cpuLimit
    }
    if (config.memoryLimit !== undefined) {
      resources.memory = config.memoryLimit
    }

    // Only add resources object if it has properties
    if (Object.keys(resources).length > 0) {
      createPayload.resources = resources
    }

    // Global deduplication based on configuration
    const dedupeKey = JSON.stringify({
      techStack: config.techStack,
      userId: config.userId,
      projectName: config.projectName,
      resources,
      // Don't include initialFiles in deduplication key to avoid cache misses
      // for different file contents but same sandbox config
    })

    // Check if identical sandbox creation is already in progress
    if (this.pendingCreations.has(dedupeKey)) {
      if (config.debug) {
        console.log('[SDK] Deduplicating identical sandbox creation:', dedupeKey)
      }
      return this.pendingCreations.get(dedupeKey)!
    }

    // Create the sandbox creation promise
    const creationPromise = this.executeCreate(config, createPayload)

    // Store the promise for deduplication
    this.pendingCreations.set(dedupeKey, creationPromise)

    try {
      return await creationPromise
    } finally {
      // Always clean up the pending creation
      this.pendingCreations.delete(dedupeKey)
    }
  }

  /**
   * Execute the actual sandbox creation logic
   */
  private static async executeCreate(
    config: SandboxConfig & SandboxClientConfig & CreateSandboxOptions,
    createPayload: any
  ): Promise<Sandbox> {
    // Use global HTTP client or create new one
    if (!this.globalHttpClient) {
      this.globalHttpClient = new HttpClient(config)
    }
    const httpClient = this.globalHttpClient

    if (config.debug) {
      console.log('[SDK] Creating sandbox with config:', createPayload)
    }

    const response = await httpClient.post('/api/sandbox/create', createPayload)

    if (!response.success || !response.data?.sandboxId) {
      throw SandboxError.creationFailed(response.error)
    }

    const sandboxId = response.data.sandboxId
    const waitForReady = config.waitForReady !== false // Default to true

    if (waitForReady) {
      // Wait for sandbox to be ready
      const sandbox = new Sandbox({
        id: sandboxId,
        status: SandboxStatus.CREATING,
        techStack: config.techStack,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        urls: {},
        config: createPayload
      }, config)

      await sandbox.waitForReady(config.timeout || 120)
      return sandbox
    } else {
      // Return immediately without waiting
      return new Sandbox({
        id: sandboxId,
        status: SandboxStatus.CREATING,
        techStack: config.techStack,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        urls: {},
        config: createPayload
      }, config)
    }
  }

  /**
   * Wait for sandbox to be ready
   */
  private async waitForReady(timeoutSeconds = 120): Promise<void> {
    const startTime = Date.now()
    const timeoutMs = timeoutSeconds * 1000

    while (Date.now() - startTime < timeoutMs) {
      await this.refresh()

      if (this.sandboxInfo?.status === SandboxStatus.RUNNING) {
        this._isReady = true
        this.emit('ready')
        return
      }

      if (this.sandboxInfo?.status === SandboxStatus.FAILED ||
          this.sandboxInfo?.status === SandboxStatus.ERROR) {
        throw SandboxError.creationFailed(`Sandbox failed to start: ${this.sandboxInfo.status}`)
      }

      // Wait before checking again
      await this.sleep(2000)
    }

    throw SandboxError.timeout('Sandbox creation', timeoutSeconds)
  }

  /**
   * Execute code in the sandbox (E2B-style main method)
   */
  async runCode(
    code: string,
    options: ExecuteCodeOptions = {}
  ): Promise<ExecutionResult> {
    this.ensureReady()

    const payload = {
      code,
      language: options.language,
      timeout: options.timeout || 30,
      filename: options.filename
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/execute`,
      payload
    )

    if (!response.success) {
      throw SandboxError.executionFailed(response.error)
    }

    // Normalize the response to match our ExecutionResult interface
    const result: ExecutionResult = {
      stdout: response.data.output || '',
      stderr: response.data.errors || '',
      exitCode: response.data.exitCode || 0,
      executionTime: response.data.executionTime || 0,
      timeout: response.data.timeout || false,
      output: response.data.output || ''
    }

    return result
  }

  /**
   * Execute code with streaming output
   */
  async streamExecution(
    code: string,
    options: StreamExecutionOptions = {}
  ): Promise<void> {
    this.ensureReady()

    if (!this.wsClient) {
      await this.initializeWebSocket()
    }

    return new Promise((resolve, reject) => {
      if (!this.wsClient) {
        reject(new Error('WebSocket client not available'))
        return
      }

      // Set up event handlers
      const handleOutput = (data: string) => {
        if (options.onOutput) options.onOutput(data)
      }

      const handleError = (data: string) => {
        if (options.onError) options.onError(data)
      }

      const handleComplete = (data: ExecutionResult) => {
        // Clean up event handlers
        this.wsClient!.off(WebSocketEventType.CODE_OUTPUT, handleOutput)
        this.wsClient!.off(WebSocketEventType.CODE_ERROR, handleError)
        this.wsClient!.off(WebSocketEventType.EXECUTION_COMPLETE, handleComplete)

        if (options.onComplete) options.onComplete(data)
        resolve()
      }

      // Register event handlers
      this.wsClient.on(WebSocketEventType.CODE_OUTPUT, handleOutput)
      this.wsClient.on(WebSocketEventType.CODE_ERROR, handleError)
      this.wsClient.on(WebSocketEventType.EXECUTION_COMPLETE, handleComplete)

      // Join sandbox room and execute code
      this.wsClient.send({
        event: WebSocketEventType.SANDBOX_STATUS,
        data: { action: 'join' },
        sandboxId: this.id
      })

      this.wsClient.send({
        event: WebSocketEventType.CODE_OUTPUT,
        data: {
          code,
          language: options.language,
          timeout: options.timeout || 30,
          filename: options.filename
        },
        sandboxId: this.id
      })
    })
  }

  /**
   * Upload a file to the sandbox
   */
  async uploadFile(filePath: string, content: string): Promise<void> {
    this.ensureReady()

    const payload = {
      filename: filePath,
      content: content,
      path: filePath
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files`,
      payload
    )

    if (!response.success) {
      throw FileError.uploadFailed(filePath, response.error)
    }
  }

  /**
   * Upload multiple files at once
   */
  async uploadFiles(files: Record<string, string>): Promise<void> {
    this.ensureReady()

    // Upload files in parallel for better performance
    const uploadPromises = Object.entries(files).map(([filePath, content]) =>
      this.uploadFile(filePath, content)
    )

    await Promise.all(uploadPromises)
  }

  /**
   * Download a file from the sandbox
   */
  async downloadFile(filePath: string): Promise<string> {
    this.ensureReady()

    const response = await this.httpClient.get(
      `/api/sandbox/${this.id}/files`,
      { path: filePath }
    )

    if (!response.success) {
      throw FileError.downloadFailed(filePath, response.error)
    }

    return response.data.content || ''
  }

  /**
   * List files in the sandbox
   */
  async listFiles(path = '/'): Promise<FileInfo[]> {
    this.ensureReady()

    const response = await this.httpClient.get(
      `/api/sandbox/${this.id}/files`,
      { path }
    )

    if (!response.success) {
      throw new FileError(`Failed to list files in ${path}: ${response.error}`)
    }

    return response.data || []
  }

  /**
   * Read a file from the sandbox with advanced options
   */
  async readFile(filePath: string, options: FileOperationOptions = {}): Promise<string | Buffer> {
    this.ensureReady()

    const params = {
      path: filePath,
      encoding: options.encoding || 'utf8'
    }

    const response = await this.httpClient.get(
      `/api/sandbox/${this.id}/files/read`,
      params
    )

    if (!response.success) {
      throw FileError.downloadFailed(filePath, response.error)
    }

    // Handle different encodings
    if (options.encoding === 'binary' || options.encoding === 'base64') {
      return Buffer.from(response.data.content, options.encoding === 'base64' ? 'base64' : 'binary')
    }

    return response.data.content || ''
  }

  /**
   * Write a file to the sandbox with advanced options
   */
  async writeFile(filePath: string, content: string | Buffer, options: FileOperationOptions = {}): Promise<void> {
    this.ensureReady()

    const encoding = options.encoding || 'utf8'
    let encodedContent: string

    if (Buffer.isBuffer(content)) {
      encodedContent = content.toString(encoding === 'binary' ? 'binary' : 'base64')
    } else {
      encodedContent = content
    }

    const payload = {
      filename: filePath,
      content: encodedContent,
      path: filePath,
      encoding: encoding,
      mode: options.mode,
      recursive: options.recursive,
      overwrite: options.overwrite !== false
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files/write`,
      payload
    )

    if (!response.success) {
      throw FileError.uploadFailed(filePath, response.error)
    }
  }

  /**
   * Create a directory
   */
  async createDirectory(dirPath: string, options: FileOperationOptions = {}): Promise<void> {
    this.ensureReady()

    const payload = {
      path: dirPath,
      recursive: options.recursive !== false,
      mode: options.mode || '0755'
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files/mkdir`,
      payload
    )

    if (!response.success) {
      throw new FileError(`Failed to create directory ${dirPath}: ${response.error}`)
    }
  }

  /**
   * Delete a file or directory
   */
  async deleteFile(path: string, options: FileOperationOptions = {}): Promise<void> {
    this.ensureReady()

    const payload = {
      path: path,
      recursive: options.recursive || false
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files/delete`,
      payload
    )

    if (!response.success) {
      throw new FileError(`Failed to delete ${path}: ${response.error}`)
    }
  }

  /**
   * Move/rename a file or directory
   */
  async moveFile(sourcePath: string, destinationPath: string, options: FileOperationOptions = {}): Promise<void> {
    this.ensureReady()

    const payload = {
      sourcePath: sourcePath,
      destinationPath: destinationPath,
      overwrite: options.overwrite || false
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files/move`,
      payload
    )

    if (!response.success) {
      throw new FileError(`Failed to move ${sourcePath} to ${destinationPath}: ${response.error}`)
    }
  }

  /**
   * Copy a file or directory
   */
  async copyFile(sourcePath: string, destinationPath: string, options: FileOperationOptions = {}): Promise<void> {
    this.ensureReady()

    const payload = {
      sourcePath: sourcePath,
      destinationPath: destinationPath,
      overwrite: options.overwrite || false,
      recursive: options.recursive || false
    }

    const response = await this.httpClient.post(
      `/api/sandbox/${this.id}/files/copy`,
      payload
    )

    if (!response.success) {
      throw new FileError(`Failed to copy ${sourcePath} to ${destinationPath}: ${response.error}`)
    }
  }

  /**
   * Get detailed file information
   */
  async getFileInfo(filePath: string): Promise<FileInfo> {
    this.ensureReady()

    const response = await this.httpClient.get(
      `/api/sandbox/${this.id}/files/stat`,
      { path: filePath }
    )

    if (!response.success) {
      throw new FileError(`Failed to get file info for ${filePath}: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get sandbox status and info
   */
  async refresh(): Promise<SandboxInfo> {
    const response = await this.httpClient.get(`/api/sandbox/${this.id}/status`)

    if (!response.success) {
      throw SandboxError.notFound(this.id)
    }

    this.sandboxInfo = {
      id: this.id,
      status: response.data.status || SandboxStatus.CREATING,
      techStack: response.data.techStack || this.sandboxInfo!.techStack,
      createdAt: response.data.createdAt || this.sandboxInfo!.createdAt,
      updatedAt: new Date().toISOString(),
      urls: response.data.result?.urls || response.data.urls || {},
      config: this.sandboxInfo!.config,
      projectId: response.data.projectId,
      commitId: response.data.commitId
    }

    if (this.sandboxInfo.status === SandboxStatus.RUNNING && !this._isReady) {
      this._isReady = true
      this.emit('ready')
    }

    return this.sandboxInfo
  }

  /**
   * Start watching a directory for changes
   */
  async watchDirectory(path: string, options: WatchOptions = {}): Promise<string> {
    this.ensureReady()

    if (!this.wsClient) {
      await this.initializeWebSocket()
    }

    const watchId = `watch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    const payload = {
      watchId,
      path,
      recursive: options.recursive !== false,
      include: options.include || ['*'],
      exclude: options.exclude || [],
      debounceMs: options.debounceMs || 100
    }

    // Send WebSocket message to start watching
    this.wsClient!.send({
      event: WebSocketEventType.DIRECTORY_WATCH_START,
      data: payload,
      sandboxId: this.id
    })

    // Set up file change event listener
    const handleFileChange = (event: FileChangeEvent) => {
      this.emit('file-change', event)
    }

    this.wsClient!.on(WebSocketEventType.FILE_CHANGE, handleFileChange)

    return watchId
  }

  /**
   * Stop watching a directory
   */
  async stopWatching(watchId: string): Promise<void> {
    if (!this.wsClient) {
      return
    }

    this.wsClient.send({
      event: WebSocketEventType.DIRECTORY_WATCH_STOP,
      data: { watchId },
      sandboxId: this.id
    })
  }

  /**
   * Upload multiple files with progress tracking
   */
  async uploadFilesWithProgress(
    files: Record<string, string | Buffer>,
    options: BulkOperationOptions = {}
  ): Promise<void> {
    this.ensureReady()

    const fileEntries = Object.entries(files)
    const totalFiles = fileEntries.length
    const totalBytes = fileEntries.reduce((sum, [, content]) => {
      return sum + (typeof content === 'string' ? Buffer.byteLength(content, 'utf8') : content.length)
    }, 0)

    const concurrency = options.concurrency || 5
    let completed = 0
    let bytesProcessed = 0

    const updateProgress = (fileName: string, fileBytes: number) => {
      completed++
      bytesProcessed += fileBytes

      const progress: OperationProgress = {
        current: completed,
        total: totalFiles,
        currentFile: fileName,
        bytesProcessed,
        totalBytes,
        percentage: Math.round((completed / totalFiles) * 100),
        estimatedTimeRemaining: completed > 0
          ? Math.round(((totalFiles - completed) / completed) * (Date.now() - startTime))
          : undefined
      }

      if (options.onProgress) {
        options.onProgress(progress)
      }
    }

    const startTime = Date.now()

    // Process files in batches
    const batches: Array<[string, string | Buffer][]> = []
    for (let i = 0; i < fileEntries.length; i += concurrency) {
      batches.push(fileEntries.slice(i, i + concurrency))
    }

    for (const batch of batches) {
      const batchPromises = batch.map(async ([filePath, content]) => {
        try {
          // Skip if file exists and skipExisting is true
          if (options.skipExisting) {
            try {
              await this.getFileInfo(filePath)
              const fileBytes = typeof content === 'string' ? Buffer.byteLength(content, 'utf8') : content.length
              updateProgress(filePath, fileBytes)
              return
            } catch {
              // File doesn't exist, continue with upload
            }
          }

          await this.writeFile(filePath, content, options.fileOptions)
          const fileBytes = typeof content === 'string' ? Buffer.byteLength(content, 'utf8') : content.length
          updateProgress(filePath, fileBytes)
        } catch (error) {
          throw new FileError(`Failed to upload ${filePath}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      })

      await Promise.all(batchPromises)
    }
  }

  /**
   * Download multiple files with progress tracking
   */
  async downloadFilesWithProgress(
    filePaths: string[],
    options: BulkOperationOptions = {}
  ): Promise<Record<string, string | Buffer>> {
    this.ensureReady()

    const result: Record<string, string | Buffer> = {}
    const totalFiles = filePaths.length
    const concurrency = options.concurrency || 5
    let completed = 0
    let bytesProcessed = 0

    const updateProgress = (fileName: string, fileBytes: number) => {
      completed++
      bytesProcessed += fileBytes

      const progress: OperationProgress = {
        current: completed,
        total: totalFiles,
        currentFile: fileName,
        bytesProcessed,
        totalBytes: 0, // We don't know total bytes until we download
        percentage: Math.round((completed / totalFiles) * 100)
      }

      if (options.onProgress) {
        options.onProgress(progress)
      }
    }

    // Process files in batches
    const batches: string[][] = []
    for (let i = 0; i < filePaths.length; i += concurrency) {
      batches.push(filePaths.slice(i, i + concurrency))
    }

    for (const batch of batches) {
      const batchPromises = batch.map(async (filePath) => {
        try {
          const content = await this.readFile(filePath, options.fileOptions)
          result[filePath] = content

          const fileBytes = typeof content === 'string' ? Buffer.byteLength(content, 'utf8') : content.length
          updateProgress(filePath, fileBytes)
        } catch (error) {
          throw new FileError(`Failed to download ${filePath}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      })

      await Promise.all(batchPromises)
    }

    return result
  }

  /**
   * Upload a directory recursively
   */
  async uploadDirectory(
    localPath: string,
    remotePath: string = '/',
    options: BulkOperationOptions = {}
  ): Promise<void> {
    // Note: This would typically work with a file system in a Node.js environment
    // For browser environments, users would need to provide file objects
    throw new Error('uploadDirectory is not yet implemented. Use uploadFilesWithProgress with pre-processed file list.')
  }

  /**
   * Download a directory recursively
   */
  async downloadDirectory(
    remotePath: string,
    options: BulkOperationOptions = {}
  ): Promise<Record<string, string | Buffer>> {
    this.ensureReady()

    // First, get all files in the directory recursively
    const allFiles = await this.listFilesRecursively(remotePath)
    const filePaths = allFiles
      .filter(file => !file.isDirectory)
      .map(file => file.path)

    return this.downloadFilesWithProgress(filePaths, options)
  }

  /**
   * List files recursively
   */
  private async listFilesRecursively(path: string, result: FileInfo[] = []): Promise<FileInfo[]> {
    const files = await this.listFiles(path)

    for (const file of files) {
      result.push(file)
      if (file.isDirectory) {
        await this.listFilesRecursively(file.path, result)
      }
    }

    return result
  }

  /**
   * Close/destroy the sandbox (E2B-style cleanup method)
   */
  async close(): Promise<void> {
    // Clean up process manager
    if (this._process) {
      this._process.cleanup()
      this._process = undefined
    }

    // Clean up file system watchers
    if (this._fs) {
      (this._fs as SandboxFileSystem).closeAll()
      this._fs = undefined
    }

    // Clean up preview manager
    if (this._previewManager) {
      this._previewManager.removeAllListeners()
      this._previewManager = undefined
    }

    // Clean up HMR manager
    if (this._hmrManager) {
      await this._hmrManager.disable()
      this._hmrManager.removeAllListeners()
      this._hmrManager = undefined
    }

    // Clean up sync manager
    if (this._syncManager) {
      await this._syncManager.disable()
      this._syncManager.removeAllListeners()
      this._syncManager = undefined
    }

    // Clean up WebSocket connection
    if (this.wsClient) {
      this.wsClient.disconnect()
      this.wsClient = undefined
    }

    if (this.sandboxInfo && this.sandboxInfo.status !== SandboxStatus.STOPPED) {
      const response = await this.httpClient.delete(`/api/sandbox/${this.id}`)

      if (!response.success) {
        console.warn(`[SDK] Failed to delete sandbox ${this.id}: ${response.error}`)
      }

      this.sandboxInfo.status = SandboxStatus.STOPPED
    }

    this._isReady = false
    this.emit('closed')
    this.removeAllListeners()
  }

  /**
   * Get sandbox ID
   */
  get id(): string {
    return this.sandboxInfo?.id || ''
  }

  /**
   * Get sandbox status
   */
  get status(): SandboxStatus | undefined {
    return this.sandboxInfo?.status
  }

  /**
   * Get sandbox URLs
   */
  get urls(): Record<string, any> {
    return this.sandboxInfo?.urls || {}
  }

  /**
   * Check if sandbox is ready
   */
  get isReady(): boolean {
    return this._isReady
  }

  /**
   * Get sandbox info
   */
  get info(): SandboxInfo | undefined {
    return this.sandboxInfo
  }

  /**
   * Get file system API (WebContainer-style interface)
   */
  get fs(): FileSystemAPI {
    if (!this._fs) {
      this._fs = new SandboxFileSystem(this.httpClient, this.id)
    }
    return this._fs
  }

  /**
   * Get process manager for shell/terminal operations
   */
  get process(): ProcessManager {
    if (!this._process) {
      this._process = new ProcessManager(this.httpClient, this.wsClient, this.id)
    }
    return this._process
  }

  /**
   * Initialize WebSocket connection
   */
  private async initializeWebSocket(): Promise<void> {
    if (this.wsClient?.isConnected()) {
      return
    }

    const authManager = this.httpClient.getAuthManager()
    this.wsClient = new WebSocketClient(authManager)

    // Forward WebSocket events
    this.wsClient.on('connected', () => this.emit('ws_connected'))
    this.wsClient.on('disconnected', () => this.emit('ws_disconnected'))
    this.wsClient.on('error', (error) => this.emit('ws_error', error))

    await this.wsClient.connect()
  }

  /**
   * Spawn a new process (convenience method)
   */
  async spawn(command: string, args: string[] = [], options: SpawnOptions = {}): Promise<SandboxProcess> {
    this.ensureReady()
    return this.process.spawn(command, args, options)
  }

  /**
   * Create an interactive shell (convenience method)
   */
  async shell(options: SpawnOptions = {}): Promise<SandboxProcess> {
    this.ensureReady()
    return this.process.shell(options)
  }

  /**
   * Execute a command and get the result (convenience method)
   */
  async exec(command: string, options: SpawnOptions = {}): Promise<{ stdout: string; stderr: string; exitCode: number }> {
    this.ensureReady()
    return this.process.exec(command, options)
  }

  /**
   * Get terminal URL for direct WebSocket connection
   */
  get terminalUrl(): string {
    const urls = this.urls
    return urls.terminal || urls['7681'] || ''
  }

  /**
   * Get preview URL for the sandbox
   */
  get previewUrl(): string {
    const urls = this.urls
    return urls.preview || urls['5173'] || urls['3000'] || ''
  }

  /**
   * Get all available URLs with their purposes
   */
  get allUrls(): Record<string, string> {
    const urls = this.urls
    const mapped: Record<string, string> = {}

    // Map known ports to their purposes
    if (urls['3000']) mapped.api = urls['3000']
    if (urls['3100']) mapped.api = urls['3100']
    if (urls['5173']) mapped.preview = urls['5173']
    if (urls['7681']) mapped.terminal = urls['7681']

    // Add all other URLs
    Object.entries(urls).forEach(([key, value]) => {
      if (!mapped[key] && !Object.values(mapped).includes(value as string)) {
        mapped[key] = value as string
      }
    })

    return mapped
  }

  /**
   * Get preview manager for error capture and monitoring
   */
  get preview(): PreviewManager {
    if (!this._previewManager) {
      this._previewManager = new PreviewManager(this.httpClient, this.id)
    }
    return this._previewManager
  }

  /**
   * Inject preview script for error capture and monitoring
   */
  async injectPreviewScript(options: PreviewScriptOptions = {}): Promise<void> {
    this.ensureReady()
    return this.preview.injectScript(options)
  }

  /**
   * Listen for preview errors and events
   */
  onPreviewError(callback: (error: PreviewError) => void): void {
    this.preview.on('error', callback)
  }

  /**
   * Listen for preview port events
   */
  onPreviewPort(callback: (port: number) => void): void {
    this.preview.on('port', callback)
  }

  /**
   * Listen for preview port change events
   */
  onPreviewPortChange(callback: (event: PortChangeEvent) => void): void {
    this.preview.on('port-change', callback)
  }

  /**
   * Get preview ready status
   */
  get previewReady(): boolean {
    return this._previewManager?.ready || false
  }

  /**
   * Get preview errors from the sandbox
   */
  async getPreviewErrors(limit = 50): Promise<PreviewError[]> {
    this.ensureReady()
    return this.preview.getPreviewErrors(limit)
  }

  /**
   * Clear preview errors
   */
  async clearPreviewErrors(): Promise<void> {
    this.ensureReady()
    return this.preview.clearPreviewErrors()
  }

  /**
   * Get HMR manager for Hot Module Reload functionality
   */
  get hmr(): HMRManager {
    if (!this._hmrManager) {
      this._hmrManager = new HMRManager(this.httpClient, this.wsClient, this.id)
    }
    return this._hmrManager
  }

  /**
   * Enable Hot Module Reload with state preservation
   */
  async enableHMR(options: HMROptions = {}): Promise<void> {
    this.ensureReady()

    if (!this.wsClient) {
      await this.initializeWebSocket()
    }

    return this.hmr.enable()
  }

  /**
   * Disable Hot Module Reload
   */
  async disableHMR(): Promise<void> {
    if (this._hmrManager) {
      return this._hmrManager.disable()
    }
  }

  /**
   * Listen for HMR updates
   */
  onHMRUpdate(callback: (update: HMRUpdate) => void): void {
    this.hmr.on('update-start', callback)
  }

  /**
   * Listen for HMR state changes
   */
  onHMRStateChange(callback: (event: 'enabled' | 'disabled' | 'connected' | 'disconnected') => void): void {
    this.hmr.on('enabled', () => callback('enabled'))
    this.hmr.on('disabled', () => callback('disabled'))
    this.hmr.on('connected', () => callback('connected'))
    this.hmr.on('disconnected', () => callback('disconnected'))
  }

  /**
   * Create HMR state snapshot
   */
  createHMRSnapshot(): HMRStateSnapshot {
    return this.hmr.createStateSnapshot()
  }

  /**
   * Restore HMR state from snapshot
   */
  restoreHMRSnapshot(snapshot?: HMRStateSnapshot): void {
    this.hmr.restoreStateSnapshot(snapshot)
  }

  /**
   * Get HMR status
   */
  get hmrEnabled(): boolean {
    return this._hmrManager?.enabled || false
  }

  get hmrConnected(): boolean {
    return this._hmrManager?.connected || false
  }

  /**
   * Get multi-tab sync manager
   */
  get sync(): MultiTabSyncManager {
    if (!this._syncManager) {
      this._syncManager = new MultiTabSyncManager(this.id)
    }
    return this._syncManager
  }

  /**
   * Enable multi-tab synchronization
   */
  async enableSync(options: SyncOptions = {}): Promise<void> {
    return this.sync.enable()
  }

  /**
   * Disable multi-tab synchronization
   */
  async disableSync(): Promise<void> {
    if (this._syncManager) {
      return this._syncManager.disable()
    }
  }

  /**
   * Sync file changes across tabs
   */
  async syncFileChange(change: FileSyncChange): Promise<void> {
    return this.sync.syncFileChange(change)
  }

  /**
   * Sync cursor position across tabs
   */
  async syncCursor(cursorInfo: CursorSyncInfo): Promise<void> {
    return this.sync.syncCursor(cursorInfo)
  }

  /**
   * Sync selection across tabs
   */
  async syncSelection(cursorInfo: CursorSyncInfo): Promise<void> {
    return this.sync.syncSelection(cursorInfo)
  }

  /**
   * Set tab as active/inactive
   */
  async setTabActive(active: boolean): Promise<void> {
    return this.sync.setTabActive(active)
  }

  /**
   * Get connected tabs
   */
  getConnectedTabs(): TabInfo[] {
    return this.sync.getConnectedTabs()
  }

  /**
   * Listen for sync events
   */
  onSyncEvent(event: string, callback: (data: any) => void): void {
    this.sync.on(event, callback)
  }

  /**
   * Get sync status
   */
  get syncEnabled(): boolean {
    return this._syncManager?.enabled || false
  }

  get syncConnected(): boolean {
    return this._syncManager?.connected || false
  }

  get connectedTabCount(): number {
    return this._syncManager?.tabCount || 1
  }

  /**
   * Ensure sandbox is ready for operations
   */
  private ensureReady(): void {
    if (!this.sandboxInfo) {
      throw new SandboxError('Sandbox not initialized')
    }

    if (!this._isReady) {
      throw SandboxError.invalidStatus('running', this.sandboxInfo.status)
    }
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}